import { EventType } from "@/analytics/analytics-events";
import { EditElementProperties } from "@/analytics/data-organization-events-properties";
import { updateProject } from "@/components/common/project-provider/update-project";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectShowSpinner } from "@/store/ui/ui-selectors";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { DatePickerVariant, DateTimePicker } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { selectIElement } from "@faro-lotv/project-source";
import {
  createMutationSetElementCreatedAt,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Box } from "@mui/material";
import {
  ComponentProps,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from "react";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";

function EditDateAction({
  elementId,
  dark,
  closeContextMenu,
}: ComponentProps<
  NonNullable<ContextMenuAction["CustomComponent"]>
>): JSX.Element | null {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectShowSpinner);
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();

  const element = useAppSelector(selectIElement(elementId));

  const initialDate = useMemo(
    () => (element ? new Date(element.createdAt) : new Date()),
    [element],
  );

  const [newDate, setNewDate] = useState<Date>();

  const updateDate = useCallback(async () => {
    try {
      if (!element || !newDate) return;
      dispatch(setShowSpinner(true));

      Analytics.track<EditElementProperties>(EventType.editElement, {
        via: "capture tree context menu",
        elementType: element.type,
        elementTypeHint: element.typeHint ?? undefined,
        hasEditedName: false,
        hasEditedTimePoint: true,
        hasEditedTags: false,
        hasEditedColor: false,
      });

      await projectApiClient.applyMutations([
        createMutationSetElementCreatedAt(element.id, newDate.toISOString()),
      ]);

      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            ids: [element.id],
          },
        }),
      );
    } catch (error) {
      handleErrorWithToast({ error, title: "Failed to update date" });
    } finally {
      dispatch(setShowSpinner(false));
      closeContextMenu();
    }
  }, [
    dispatch,
    element,
    handleErrorWithToast,
    newDate,
    projectApiClient,
    closeContextMenu,
  ]);

  if (!element) return null;

  return (
    <Box aria-label="edit element date" component="span">
      <DateTimePicker
        variant={dark ? DatePickerVariant.Dark : DatePickerVariant.Light}
        fieldVariant="standard"
        date={newDate}
        initialDate={initialDate}
        onChange={setNewDate}
        onAccept={updateDate}
        disabled={isLoading}
        formSx={{
          width: "100%",
          // Add some margin to the input and buttons to add space on the sides
          ".MuiInputBase-input": { ml: 2 },
          ".MuiIconButton-root": { mr: 1.5 },
        }}
      />
    </Box>
  );
}

/**
 * Action to edit the date of an element.
 * Renders a custom component to show a date picker.
 */
export const EDIT_DATE_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.editDate,
  CustomComponent: EditDateAction,
};
